<template>
  <transition name="cm">
    <div v-if="modelValue" class="cm-overlay" @click.self="cancel" @keydown.esc="cancel">
      <div class="cm-dialog" role="dialog" aria-modal="true" :aria-label="title" ref="dialog" tabindex="-1">
        <div class="cm-icon" :class="{ danger }">
          <i :class="icon" aria-hidden="true"></i>
        </div>
        <h2 class="cm-title">{{ title }}</h2>
        <p v-if="message" class="cm-message">{{ message }}</p>
        <input
          v-if="mode === 'prompt'"
          type="text"
          class="cm-input"
          ref="input"
          v-model="localInputValue"
          :placeholder="placeholder"
          @keydown.enter="confirm"
        />
        <div class="cm-actions">
          <button v-if="mode !== 'alert'" type="button" class="cm-cancel" @click="cancel">{{ cancelLabel }}</button>
          <button type="button" class="cm-confirm" :class="{ danger }" @click="confirm">{{ mode === 'alert' ? okLabel : confirmLabel }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// Small themed dialog for the public site and dashboard, a replacement for
// window.alert()/window.confirm()/window.prompt(). `mode` controls which of
// the three it behaves as:
//  - "confirm" (default): Cancel + Confirm buttons, emits "confirm" with no payload.
//  - "alert": a single dismiss button (no Cancel), emits "confirm" with no payload.
//  - "prompt": adds a text input; emits "confirm" with the typed string as payload.
export default {
  name: "ConfirmModal",
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: "" },
    message: { type: String, default: "" },
    mode: { type: String, default: "confirm" }, // "alert" | "confirm" | "prompt"
    confirmLabel: { type: String, default: "Confirm" },
    cancelLabel: { type: String, default: "Cancel" },
    okLabel: { type: String, default: "OK" },
    icon: { type: String, default: "fas fa-sign-out-alt" },
    danger: { type: Boolean, default: false },
    inputValue: { type: String, default: "" },
    placeholder: { type: String, default: "" },
  },
  emits: ["update:modelValue", "update:inputValue", "confirm", "cancel"],
  data() {
    return {
      localInputValue: this.inputValue,
    };
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.localInputValue = this.inputValue;
        this.$nextTick(() => {
          const target = this.mode === "prompt" ? this.$refs.input : this.$refs.dialog;
          target && target.focus();
        });
      }
    },
  },
  methods: {
    confirm() {
      if (this.mode === "prompt") {
        this.$emit("update:inputValue", this.localInputValue);
        this.$emit("confirm", this.localInputValue);
      } else {
        this.$emit("confirm");
      }
      this.$emit("update:modelValue", false);
    },
    cancel() {
      this.$emit("update:modelValue", false);
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
.cm-overlay {
  --primary-color: #8dbb1a;
  --text-color: #757575;
  --heading-color: #1f2a1a;
  --background-light: #f9ffeb;
  --line: #e2e6da;
  --font-family: "Inter", sans-serif;
  --ease-out: cubic-bezier(0.2, 0.7, 0.2, 1);

  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(31, 42, 26, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: var(--font-family);
}

.cm-dialog {
  width: min(420px, 100%);
  background: #fff;
  border-radius: 18px;
  padding: 32px 28px 24px;
  text-align: center;
  box-shadow: 0 24px 60px rgba(31, 42, 26, 0.3);
  outline: none;
}

.cm-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-light);
  color: var(--primary-color);
  font-size: 24px;
}

.cm-icon.danger {
  background: #fff1f0;
  color: #dc3545;
}

.cm-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--heading-color);
  margin: 0 0 6px;
}

.cm-input {
  width: 100%;
  box-sizing: border-box;
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 9px;
  border: 1.5px solid var(--line);
  font-family: var(--font-family);
  font-size: 15px;
  color: var(--heading-color);
}

.cm-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.cm-message {
  font-size: 15px;
  color: var(--text-color);
  line-height: 1.5;
  margin: 0;
}

.cm-actions {
  display: flex;
  gap: 10px;
  margin-top: 24px;
}

.cm-cancel,
.cm-confirm {
  flex: 1;
  padding: 12px 16px;
  border-radius: 9px;
  font-family: var(--font-family);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 150ms ease, filter 150ms ease, background 150ms ease;
}

.cm-cancel {
  background: #fff;
  border: 1.5px solid var(--line);
  color: var(--heading-color);
}

.cm-cancel:hover {
  background: var(--background-light);
}

.cm-confirm {
  background: var(--primary-color);
  border: 1.5px solid var(--primary-color);
  color: #fff;
}

.cm-confirm.danger {
  background: #dc3545;
  border-color: #dc3545;
}

.cm-confirm:hover {
  filter: brightness(0.94);
  transform: translateY(-1px);
}

.cm-enter-active,
.cm-leave-active {
  transition: opacity 200ms ease;
}

.cm-enter-active .cm-dialog,
.cm-leave-active .cm-dialog {
  transition: transform 240ms var(--ease-out);
}

.cm-enter-from,
.cm-leave-to {
  opacity: 0;
}

.cm-enter-from .cm-dialog,
.cm-leave-to .cm-dialog {
  transform: translateY(12px) scale(0.98);
}
</style>
